import { collection, query, where, getDocs } from 'firebase/firestore';
import { getStorage, ref, getBytes } from 'firebase/storage';
import { db, auth } from '../firebase'; // Importiamo anche auth
import { getSessionBySubImageDetails, saveSession } from '../indexedDB';

const storage = getStorage();

// Estrae il path relativo da un Firebase Storage download URL
// es. "images/uid/filename.jpg"
function extractStoragePath(downloadURL) {
  try {
    const url = new URL(downloadURL);
    const match = url.pathname.match(/\/o\/(.+)$/);
    if (!match) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

// Ricava il MIME type dall'estensione del file
function mimeTypeFromPath(path) {
  const ext = path.split('.').pop().toLowerCase();
  const map = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg',
    png: 'image/png', gif: 'image/gif',
    webp: 'image/webp', tif: 'image/tiff', tiff: 'image/tiff',
  };
  return map[ext] || 'image/jpeg';
}

// Scarica l'immagine tramite SDK Firebase (aggira il CORS sui download URL con token)
async function fetchImageAsDataURL(downloadURL) {
  const storagePath = extractStoragePath(downloadURL);
  if (storagePath) {
    try {
      const storageRef = ref(storage, storagePath);
      const bytes = await getBytes(storageRef);
      const mimeType = mimeTypeFromPath(storagePath);
      return new Promise((resolve, reject) => {
        const blob = new Blob([bytes], { type: mimeType });
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (sdkErr) {
      console.warn('Firebase SDK download fallito, tentativo con fetch diretto:', sdkErr);
    }
  }
  // Fallback: fetch diretto (funziona solo se CORS è configurato sul bucket)
  const response = await fetch(downloadURL);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function isValidDataURL(dataURL) {
  return typeof dataURL === 'string' && dataURL.startsWith('data:image');
}

async function processSession(docSnapshot) {
  const firebaseSession = docSnapshot.data();
  const originalImageName = firebaseSession.subImage.originalImageName;
  const firebaseName = firebaseSession.subImage.name;

  console.log(`Controllo sessione: ${originalImageName}, ${firebaseName}`);

  // Verifica se esiste in IndexedDB un record con gli stessi dettagli
  const localSession = await getSessionBySubImageDetails(firebaseName, originalImageName, firebaseSession.email);

  // Salta solo se la sessione locale esiste E ha un'immagine valida
  if (localSession && isValidDataURL(localSession.subImage?.dataURL)) {
    console.log(`Sessione già esistente localmente: ${originalImageName}`);
    return;
  }

  if (localSession) {
    console.log(`Sessione trovata ma senza dataURL valido, ri-sincronizzazione: ${originalImageName}`);
  }

  // Se subImage.dataURL è null e c'è un downloadURL, scarica e converte l'immagine
  if (firebaseSession.subImage && !firebaseSession.subImage.dataURL && firebaseSession.subImage.downloadURL) {
    try {
      firebaseSession.subImage.dataURL = await fetchImageAsDataURL(firebaseSession.subImage.downloadURL);
    } catch (err) {
      console.error(`Errore nella conversione dell'immagine ${firebaseSession.subImage.name}:`, err);
      return; // Continua con la prossima sessione
    }
  }

  // Salva la sessione in IndexedDB (il downloadURL viene mantenuto così saveSession
  // può evitare di ri-caricare l'immagine su Firebase Storage)
  await saveSession(firebaseSession);
  console.log(`Sincronizzata sessione: ${originalImageName}`);
}

export async function syncSessionsFromFirebase(uid) {
  try {
    const sessionsRef = collection(db, 'sessions');
    let q;
    // Se l'utente è superadmin, recupera tutti i documenti;
    // altrimenti, filtra per userId
    if (auth.currentUser && auth.currentUser.email === "dentaidenai6@gmail.com") {
      q = query(sessionsRef);
    } else {
      q = query(sessionsRef, where('userId', '==', uid));
    }

    const querySnapshot = await getDocs(q);
    const docSnapshots = querySnapshot.docs;

    // Numero massimo di operazioni in contemporanea
    const maxConcurrent = 10;
    let activePromises = 0;
    let index = 0;

    const executeSessionProcessing = async () => {
      if (index < docSnapshots.length && activePromises < maxConcurrent) {
        activePromises++;
        const currentIndex = index++; // Incrementa l'indice e crea una copia locale
        processSession(docSnapshots[currentIndex])
          .then(() => {
            activePromises--;
          })
          .catch((err) => {
            console.error(`Errore nella sincronizzazione della sessione:`, err);
            activePromises--;
          })
          .finally(executeSessionProcessing);
      } else if (activePromises > 0) {
        // Attendi brevemente prima di ricontrollare
        setTimeout(executeSessionProcessing, 100);
      }
    };

    await executeSessionProcessing();
  } catch (error) {
    console.error('Errore nella sincronizzazione delle sessioni:', error);
  }
}
